import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Document, Page, View, Text, Image } from '@react-pdf/renderer';
import { styles } from './service/styles';
import { getImageSrc } from '../../../../utils/ImageHelper';
import { useStore } from '../../../../app/stores/store';

type MetaTableCell = {
  label?: string;
  l?:string;
  value?: any;
  checkboxOptions?: { optionName: string; optionIndex: number }[];
};

type DeliverablesReportsPDFProps = {
  localElement: any[];
  metadata: any[];
  isDualPDF:boolean;
};

export type DeliverablesQCData = {
  _uuid_: string;
  QC_File_Format: string;
  Format: string;
  Resolution: string;
  Format_Profile: string;
  Frame_Rate: string;
  Colour_Space: string;
  Image_Aspect_Ratio: string;
  Container_Aspect_Ratio: string;
  Bit_Depth: string;
  Restored_Videomix_24_bit_48_KHZ_Used: string;
  Audio_Channel: string;
  Audio_Sync: string;
  Checksum_Algorithm_Used: string;
  Automated_QC_Report_Checked: string;
  Total_File_Size: string;
  SRT_File_Available: string;
  Subtitle_Font_and_Font_Size: string;
  SRT_Sync: string;
  Watermark: string;
  File_Creation_Date: string;
  'Fixity_/_Checksum': string;
  'Remarks_by_Vendor_QC_Technician/Supervisor': string;
  'Approval_of_Vendor_QC_Technician/Supervisor': string;
  'Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)': string;
  'Approval_of_Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)': string;
  Performed_By: string;
  Date_of_QC: string;
  Approved_By: string;
  Date_of_Approval: string;
  QUALITY_CHECKED_BY_NFHM_DELIVERABLES_SUPERVISOR: string;
  Date_of_Quality_Checked_By_NFHM_Supervisor: string;
}

const linearHandler = (str:string) => {
  str = str.replace(/LOG/gi, "LINEAR");
   if (!str.includes("LINEAR")) {
    str = "LINEAR|" + str.replace(/^\|/, "");
  }

  return str;
}
const renderCheckbox = (cellLabel: string, options: { optionName: string; optionIndex: number }[], value: string) => {
  if(cellLabel === 'SOURCE DPX') value = linearHandler(value);
  const selectedOptions = value?.split('|').map(v => v.trim()) ?? [];
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
      {options
        .sort((a, b) => a.optionIndex - b.optionIndex)
        .map((option) => {
          const isChecked = selectedOptions.includes(option.optionName);
          const checkboxImg = isChecked
            ? '../assets/NFAI_Restoration_Checkbox_Checked.png'
            : '../assets/NFAI_Restoration_Checkbox_Unchecked.png';

          return (
            <View key={option.optionIndex} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 4 }}>
              <Image style={{ width: 10, height: 10 }} src={checkboxImg} />
              <Text style={{ marginLeft: 2 }}>{option.optionName}</Text>
            </View>
          );
        })}
    </View>
  );
};

const handleCheckbox = (
  name: string,
  options: { optionName: string; optionIndex: number }[],
  value: string
): JSX.Element => {
  const selectedOptions = value?.split('|') ?? [];

  return (
    <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'flex-start', borderLeftWidth: 0, borderBottomWidth: 0, borderRightWidth: 0 }]}>
      {options
        .sort((a, b) => a.optionIndex - b.optionIndex)
        .map((option) => {
          const isChecked = selectedOptions.includes(option.optionName);
          const checkboxImage = isChecked
            ? '../assets/NFAI_Restoration_Checkbox_Checked.png'
            : '../assets/NFAI_Restoration_Checkbox_Unchecked.png';
          const qcCheckboxImage = isChecked
            ? '../assets/checked.jpg'
            : '../assets/unchecked.png'
          const isQC = name.includes('Approval');
          const checkboxImg = isQC ? checkboxImage : qcCheckboxImage;

          return (
            <View key={option.optionIndex} style={styles.checkboxContainer}>
              <Image style={styles.smallLogo} src={checkboxImg} />
              <Text style={[styles.imgContent, {marginLeft: 2}]}>{option.optionName}</Text>
            </View>
          );
        })}
    </View>
  );
};


const DeliverablesReport: React.FC<DeliverablesReportsPDFProps> = ({ localElement, metadata, isDualPDF}) => {
  const { masteringStore } = useStore();
  // const [tableData, setTableData] = useState<DeliverablesQCData[]>([]);
  const [resolution, setResolution] = useState<string>('');

  useEffect(() => {
    const extractQCData = (elements: any[]): DeliverablesQCData[] => {
      const result: DeliverablesQCData[] = [];

      elements.forEach((element) => {
        if (element.elementName === 'Resolution'){
          const value = element.elementValue;
          setResolution(value)
        }
        if (element.elementName === 'QC REPORT') {
          const value = element.elementValue;

          if (typeof value !== 'string' || !value.includes('|')) {
            return;
          }

          try {
            const parts = value.split('|');
            const jsonString = parts.slice(1).join('|');
            const parsedArray = JSON.parse(jsonString);

            if (Array.isArray(parsedArray)) {
              const mapped = parsedArray.map((el: any) => ({
                _uuid_: el['_uuid_'] || '',
                QC_File_Format: el['QC_File_Format'] || '',
                Format: el['Format'] || '',
                Resolution: el['Resolution'] || '',
                Format_Profile: el['Format_Profile'] || '',
                Frame_Rate: el['Frame_Rate'] || '',
                Colour_Space: el['Colour_Space'] || '',
                Image_Aspect_Ratio: el['Image_Aspect_Ratio'] || '',
                Container_Aspect_Ratio: el['Container_Aspect_Ratio'] || '',
                Bit_Depth: el['Bit_Depth'] || '',
                Restored_Videomix_24_bit_48_KHZ_Used: el['Restored_Videomix_24_bit_48_KHZ_Used'] || '',
                Audio_Channel: el['Audio_Channel'] || '',
                Audio_Sync: el['Audio_Sync'] || '',
                Checksum_Algorithm_Used: el['Checksum_Algorithm_Used'] || '',
                Automated_QC_Report_Checked: el['Automated_QC_Report_Checked'] || '',
                Total_File_Size: el['Total_File_Size'] || '',
                SRT_File_Available: el['SRT_File_Available'] || '',
                Subtitle_Font_and_Font_Size: el['Subtitle_Font_and_Font_Size'] || '',
                SRT_Sync: el['SRT_Sync'] || '',
                Watermark: el['Watermark'] || '',
                File_Creation_Date: el['File_Creation_Date'] || '',
                'Fixity_/_Checksum': el['Fixity_/_Checksum'] || '',
                'Remarks_by_Vendor_QC_Technician/Supervisor': el['Remarks_by_Vendor_QC_Technician/Supervisor'] || '',
                'Approval_of_Vendor_QC_Technician/Supervisor': el['Approval_of_Vendor_QC_Technician/Supervisor'] || '',
                'Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)': el['Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)'] || '',
                'Approval_of_Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)': el['Approval_of_Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)'] || '',
                Performed_By: el['Performed_By'] || '',
                Date_of_QC: el['Date_of_QC'] || '',
                Approved_By: el['Approved_By'] || '',
                Date_of_Approval: el['Date_of_Approval'] || '',
                QUALITY_CHECKED_BY_NFHM_DELIVERABLES_SUPERVISOR: el['QUALITY_CHECKED_BY_NFHM_DELIVERABLES_SUPERVISOR'] || '',
                Date_of_Quality_Checked_By_NFHM_Supervisor: el['Date_of_Quality_Checked_By_NFHM_Supervisor'] || '',
              }));

              result.push(...mapped);
            }
          } catch (err) {
            console.error('Failed to parse QC Details JSON:', err);
          }
        }
      });

      const seen = new Set<string>();
      const uniqueResults = result.filter((item) => {
        if (seen.has(item._uuid_)) return false;
        seen.add(item._uuid_);
        return true;
      });

      return uniqueResults;
    };

    const combinedElements = [...metadata, ...localElement];
    const extractedQCData = extractQCData(combinedElements);
    // console.log('Extracted QC Data:', extractedQCData);
    // setTableData(extractedQCData);
    masteringStore.deliveriableInfo = extractedQCData;
     //eslint-disable-next-line
  }, [metadata, localElement, isDualPDF]);

  const getMetaDataValue = (metaData: any[], key: string) => {
    const meta = metaData.find((item) => item.elementName === key);
    return meta ? meta.elementValue : '';
  };

  const  convertToMinutesAndSeconds = (timecode: string) => {
    if (!timecode || typeof timecode !== 'string' || timecode.trim() === '') {
      return '00 MINS 00 SECS';
    }
    // eslint-disable-next-line 
    const [hh, mm, ss, ff] = timecode.split(':').map(Number);
    const totalMinutes = hh * 60 + mm;
    const result = `${totalMinutes} MINS ${ss} SECS`;
    return result;
  }

  const formatDate = (inputDate: string) => {
    const [month, day, year] = inputDate.split("-");
    const formattedDate = `${day}-${month}-${year}`;
    return formattedDate;
  }

  const orangeRow = () => {
    return (
      <View style={{
        height: 10,
        backgroundColor: '#f4a261',
        borderWidth: 1,
        borderColor: 'black',
        margin: 0,
        padding: 0,
        width: '100%',
      }} />
    );
  };
    
  const metaTableData: MetaTableCell[][] = [
    [ { label: 'PROJECT TITLE :' }, {value: getMetaDataValue(metadata, 'Film_Title')}, {label: 'DIRECTOR:'}, {value: getMetaDataValue(metadata, 'Director')} ],
    [ { label: 'PROJECT ID/CODE :' }, {value: getMetaDataValue(metadata, 'UCCID')}, {label: 'YEAR OF RELEASE/ PRODUCTION:'}, {value: getMetaDataValue(metadata, 'Film_Year')} ],
    [ { label: 'LANGUAGE' }, {value: getMetaDataValue(metadata,'Language')}, {}, {} ],
    [ { label: 'SUBTITLE LANGUAGE' }, {value: getMetaDataValue(metadata, 'SUBTITLE LANGUAGE')}, { label: 'RESOLUTION:' }, {value: getMetaDataValue(metadata, 'Resolution'), checkboxOptions: [{ optionName: '2K', optionIndex: 0 },{ optionName: '4K', optionIndex: 1 }]} ],
    [ { label: 'SOURCE DPX' }, {l: 'SOURCE DPX', value: getMetaDataValue(metadata, 'Film Type') + '|' + getMetaDataValue(metadata, 'Film Scanning Type'), checkboxOptions: [{ optionName: 'B & W', optionIndex: 0 },{ optionName: 'Colour', optionIndex: 1 },{ optionName: 'LOG', optionIndex: 2 },{ optionName: 'LINEAR', optionIndex: 3 }]}, { label: 'BIT DEPTH:' }, {value:`${(getMetaDataValue(metadata, 'Bit Depth') || '').split('|').map((x:any) => `${x} bit`).join('|')}`, checkboxOptions: [{ optionName: '10 bit', optionIndex: 0 },{ optionName: '16 bit', optionIndex: 1 }]} ],
    [ { label: 'SOURCE DPX COLOR SPACE' }, {value: getMetaDataValue(metadata, 'SOURCE DPX COLOR SPACE')}, {}, {} ],
    [ { label: 'SOURCE DPX ORIGIN:' }, {value: getMetaDataValue(metadata, 'Film Gauge')}, {}, {} ],
    [ { label: 'SOURCE AUDIO FORMAT' }, {value: getMetaDataValue(metadata, 'SOURCE AUDIO FORMAT')}, {}, {} ],
    [ { label: 'NO REELS' }, {value: getMetaDataValue(metadata, 'Total Reels')}, {label: 'Length (as per censor/CBFC certificate):'}, {value: getMetaDataValue(metadata, 'Length-Meters/Feet (as per CBFC)')} ],
    [ { label: 'Duration (Synced Title in Mints)' }, {value: convertToMinutesAndSeconds(getMetaDataValue(metadata, 'DCP TOTAL RUN TIME'))}, {}, {} ]
  ];

  const renderTable = () => {
    return metaTableData.map((row, rowIndex) => (
      <View key={rowIndex} style={styles.tableRow}>
        {row.map((cell: MetaTableCell, cellIndex) => (
          <View key={cellIndex} style={[styles.tableCell, { borderTopWidth: 0, borderRightWidth: cellIndex === row.length - 1 ? 1 : 0 }]}>
            {cell.label && <Text>{cell.label}</Text>}
            {cell.checkboxOptions
              ? renderCheckbox(cell.l ?? '', cell.checkboxOptions, cell.value ?? '')
              : <Text>{cell.value ?? ''}</Text>
            }
          </View>
        ))}
      </View>
    ));
  };

  const QCTable: React.FC<{ data: DeliverablesQCData, bitRate?: string }> = ({ data, bitRate }) => {
    return (
      <View style={styles.section}>
        <View style={{backgroundColor: '#d9d9d9'}}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.bold, { borderRightWidth: 0 }]}>QC File Format</Text>
            <Text style={[styles.tableCell, styles.bold, { borderRightWidth: 0 }]}>{data.QC_File_Format ?? ''}</Text>
            <Text style={[styles.tableCell, styles.bold, { borderLeftWidth: 0, borderRightWidth: 0, textDecoration: 'underline' }]}>
              ** QC needs to be done by 100% watch through.
            </Text>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { borderRightWidth: 0, borderBottomWidth: 0}]}></Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderRightWidth: 0 }]}>
              <Text>Format: {data.Format}</Text>
              {handleCheckbox('Format', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 }
              ], data.Format !== '' ? 'Yes' : 'No')}
            </View>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderRightWidth: 0 }]}>
              <Text>Resolution ({data.Resolution})</Text>
              {handleCheckbox('Resolution', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 }
              ], data.Resolution !== '' ? 'Yes' : 'No')}
            </View>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { borderRightWidth: 0, borderBottomWidth: 0}]}></Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderRightWidth: 0 }]}>
              <Text>Format Profile: {data.Format_Profile ?? ''}</Text>
              {handleCheckbox('Format_Profile', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 }
              ], data.Format_Profile !== '' ? 'Yes' : 'No')}
            </View>
            <Text style={[styles.tableCell, { borderRightWidth: 0 }]}></Text>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { borderRightWidth: 0, borderBottomWidth: 0}]}></Text>
            <Text style={[styles.tableCell, { borderRightWidth: 0 }]}>Frame rate: {data.Frame_Rate ?? ''}</Text>
            <Text style={[styles.tableCell]}>Colour Space: {data.Colour_Space ?? ''}</Text>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { borderRightWidth: 0, borderBottomWidth: 0}]}></Text>
            <Text style={[styles.tableCell, { borderRightWidth: 0 }]}>Image Aspect Ratio: {data.Image_Aspect_Ratio ?? ''}</Text>
            <Text style={[styles.tableCell, { borderRightWidth: 0 }]}>Container Aspect Ratio: {data.Container_Aspect_Ratio ?? ''}</Text>
            <Text style={[styles.tableCell]}>Bit Depth ({data.Bit_Depth ?? ''})</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { borderRightWidth: 0, borderBottomWidth: 0}]}></Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderRightWidth: 0 }]}>
              <Text>Restored Videomix 24 {'\n'}bit 48 KHZ Used</Text>
              {handleCheckbox('Restored_Videomix_24_bit_48_KHZ_Used', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 }
              ], data.Restored_Videomix_24_bit_48_KHZ_Used  ?? 'No')}
            </View>
            <Text style={[styles.tableCell]}>Audio Channel: {data.Audio_Channel ?? ''}</Text>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { borderRightWidth: 0 }]}></Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}]}>
              <Text>Audio Sync</Text>
              {handleCheckbox('Audio_Sync', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 }
              ], data.Audio_Sync ?? 'No')}
            </View>
            <Text style={[styles.tableCell, {borderLeftWidth: 0, borderRightWidth: 0}]}></Text>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell]}>Checksum algorithm used</Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap', borderRightWidth: 0, borderLeftWidth: 0 }]}>
              {handleCheckbox('Checksum_Algorithm_Used', [
                { optionName: 'MD5', optionIndex: 0 },
                { optionName: 'SHA-256', optionIndex: 1 },
                { optionName: 'ANYOTHER', optionIndex: 2 }
              ], data.Checksum_Algorithm_Used)}
            </View>
            <Text style={[styles.tableCell, {borderLeftWidth: 0, borderRightWidth: 0}]}></Text>
            <Text style={[styles.tableCell, {borderLeftWidth: 0}]}></Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell]}>Automated QC report checked (If any)</Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderLeftWidth: 0}]}>
            {handleCheckbox('Automated_QC_Report_Checked', [
              { optionName: 'Yes', optionIndex: 0 },
              { optionName: 'No', optionIndex: 1 }
            ], data.Automated_QC_Report_Checked ?? 'No')}
            </View>
            <Text style={[styles.tableCell, {borderLeftWidth: 0, borderRightWidth: 0}]}>Bit Rate - (Should be max. - {bitRate ?? '200 to 250mbps'})</Text>
            <Text style={styles.tableCell}>Total File Size: {data.Total_File_Size ?? ''}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell]}>SRT File Available</Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap',borderLeftWidth: 0 , paddingRight:'3px'}]}>
            {handleCheckbox('SRT_File_Available', [
              { optionName: 'Yes', optionIndex: 0 },
              { optionName: 'No', optionIndex: 1 },
              { optionName: 'NA', optionIndex: 2 }
            ], data.SRT_File_Available ?? 'NA')}
            </View>
            <Text style={[styles.tableCell, {borderLeftWidth: 0, borderRightWidth: 0}]}>Subtitle Font and Font Size: {data.Subtitle_Font_and_Font_Size ?? 'NA'}</Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', paddingRight:'3px'}]}>
              <Text>SRT Sync</Text>
              {handleCheckbox('SRT_Sync', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 },
                { optionName: 'NA', optionIndex: 2 }
              ], data.SRT_Sync ?? 'NA')}
            </View>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell]}>Watermark</Text>
            <View style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap',borderLeftWidth: 0 }]}>
              {handleCheckbox('Watermark', [
                { optionName: 'Yes', optionIndex: 0 },
                { optionName: 'No', optionIndex: 1 }
              ], data.Watermark ?? 'No')}
            </View>
            <Text style={[styles.tableCell, {borderLeftWidth: 0, borderRightWidth: 0}]}></Text>
            <Text style={[styles.tableCell]}>File Creation Date: {formatDate(data.File_Creation_Date) ?? ''}</Text>
          </View>
          <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
            <Text style={[styles.tableCell, {flex: 0.9766, borderRightWidth: 0}]}>Fixity / Checksum</Text>
            <Text style={[styles.tableCell, { flex: 3 }]}>{data['Fixity_/_Checksum'] ?? ''}</Text>
          </View>
        </View>
        <View style={[styles.remarksBox, {borderTopWidth: 0}]}>
          <Text style={{ marginTop: 5, marginLeft: 5, marginBottom: 8 }}>
            Remarks by Vendor QC Technician/Supervisor:
          </Text>
          <Text style={{ marginTop: 5, marginLeft: 5, marginBottom: 12 }}>
            {data['Remarks_by_Vendor_QC_Technician/Supervisor'] ?? 'No remarks available'}
          </Text>
        </View>

        <View style={[styles.remarksBox]}>
          <Text style={{ marginTop: 5, marginLeft: 5, marginBottom: 8 }}>
            Any other Remarks by NFHM QC Supervisor (DOP):
          </Text>
          <Text style={{ marginTop: 5, marginLeft: 5, marginBottom: 12 }}>
            {data['Any_other_Remarks_by_NFHM_QC_Supervisor_(DOP)'] ?? 'No remarks available'}
          </Text>
        </View>
        <View style={{ borderWidth: 1, borderColor: '#000' }}>
          <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#000' }}>
            <View style={{ flex: 1, borderRightWidth: 1, borderColor: '#000', padding: 5 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Text>
                  Performed by: <Text>{data.Performed_By ?? ''}</Text>
                </Text>
                <Image 
                  src={
                    data.Performed_By?.toLowerCase()?.includes('riaz khan') 
                    ? getImageSrc('riaz khan') 
                    : data.Performed_By?.toLowerCase()?.includes('siddharth') 
                    ? getImageSrc('siddharth')
                    : data.Performed_By?.toLowerCase()?.includes('kalai') 
                    ? getImageSrc('kalai')
                    : data.Performed_By?.toLowerCase()?.replaceAll(' ','')?.includes('arulkumar')
                    ? getImageSrc('arulkumar')
                    : data.Performed_By?.toLowerCase()?.includes('karthick') 
                    ? getImageSrc('karthick')
                    : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=="
                  } 
                  style={{ width: 60, height: 30 }} 
                />
              </View>
              <Text>Date of QC ({formatDate(data.Date_of_QC)}) and Signature:</Text>
            </View>
            <View style={{ flex: 1, padding: 5 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Text>
                  Approved by: <Text>{data.Approved_By ?? ''}</Text>
                </Text>
                <Image 
                  src={
                    data.Approved_By?.toLowerCase()?.includes('riaz khan') 
                    ? getImageSrc('riaz khan') 
                    : data.Approved_By?.toLowerCase()?.includes('siddharth') 
                    ? getImageSrc('siddharth')
                    : data.Approved_By?.toLowerCase()?.includes('kalai') 
                    ? getImageSrc('kalai')
                    : data.Approved_By?.toLowerCase()?.replaceAll(' ','')?.includes('arulkumar')
                    ? getImageSrc('arulkumar')
                    : data.Approved_By?.toLowerCase()?.includes('karthick') 
                    ? getImageSrc('karthick')
                    : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=="
                  }                  
                  style={{ width: 60, height: 30 }} 
                />
              </View>
              <Text>Date ({formatDate(data.Date_of_Approval)}) and Signature:</Text>
            </View>
          </View>
          <View style={{ padding: 5 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Text>
                QUALITY CHECKED BY NFHM DELIVERABLES SUPERVISOR: <Text>{data.QUALITY_CHECKED_BY_NFHM_DELIVERABLES_SUPERVISOR ?? ''}</Text>
              </Text>
              <Image 
                src={
                  data.QUALITY_CHECKED_BY_NFHM_DELIVERABLES_SUPERVISOR?.toLowerCase()?.includes('pramod')
                  ? getImageSrc('pramod')
                  : data.QUALITY_CHECKED_BY_NFHM_DELIVERABLES_SUPERVISOR?.toLowerCase()?.includes('rajan') 
                  ? getImageSrc('rajan')
                  : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=="
                } 
                style={{ width: 50, height: 20 }}
              />
            </View>
            <Text>Date ({formatDate(data.Date_of_Quality_Checked_By_NFHM_Supervisor)}) and Signature:</Text>
          </View >
          
        </View>
        {isDualPDF ? <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end', marginTop: '2px'}}>
            <Image 
              src={getImageSrc('bala')} 
              style={{ width: 50, height: 30 }} 
            />
          </View> : null}
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, styles.border_Right, { backgroundColor: 'gold', justifyContent: 'center', alignItems: 'center', borderTopWidth: 1 }]}>
                <Text style={{ fontWeight: 'bold' }}>DELIVERABLES QUALITY CHECK REPORT</Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={[styles.tableCell, styles.border_Right, { backgroundColor: '#d9d9d9', justifyContent: 'center', alignItems: 'center' }]}>
                <Text style={{ fontWeight: 'bold' }}>NFHM Restoration Project</Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={[styles.tableCell, styles.border_Right, { backgroundColor: '#d9d9d9', justifyContent: 'center', alignItems: 'center' }]}>
                <Text style={{ fontWeight: 'bold' }}>PICTURE VENDOR NAME: Prasad Corporation Pvt Ltd, Chennai</Text>
              </View>
            </View>
            {renderTable()}
            {orangeRow()}
            {masteringStore.deliveriableInfo[0] && <QCTable data={masteringStore.deliveriableInfo[0]} />}
          </View>
        </View>
      </Page>

      {(masteringStore.deliveriableInfo[1]) && (
        <Page size="A4" style={styles.page}>
          <View style={styles.container}>
            <View style={styles.table}>
              {orangeRow()}
              {masteringStore.deliveriableInfo[1] && <QCTable data={masteringStore.deliveriableInfo[1]} bitRate={resolution === '2K' ? '35mbps': '200 to 250mbps'} />}
            </View>
          </View>
        </Page>
      )}

      {(masteringStore.deliveriableInfo[2]) && (
        <Page size="A4" style={styles.page}>
          <View style={styles.container}>
            <View style={styles.table}>
              {orangeRow()}
              {masteringStore.deliveriableInfo[2] && <QCTable data={masteringStore.deliveriableInfo[2]} bitRate={resolution === '2K' ? '25mbps': '35mbps'} />}
            </View>
          </View>
        </Page>
      )}

      {(masteringStore.deliveriableInfo[3]) && (
        <Page size="A4" style={styles.page}>
          <View style={styles.container}>
            <View style={styles.table}>
              {orangeRow()}
              {masteringStore.deliveriableInfo[3] && <QCTable data={masteringStore.deliveriableInfo[3]} bitRate='25mbps' />}
            </View>
          </View>
        </Page>
      )}
    </Document>
  );
}

export default observer(DeliverablesReport)